import React from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import get from 'lodash/get';

import { Col } from 'modsy-ui-kit/build/Grid';
import { Box, Flex } from 'web-ui-kit/containers';
import { PDP_MODAL_Z_INDEX } from 'web-ui-kit/zIndexes';
import { H7, primaryFont } from 'web-ui-kit/heading';
import { burnishedSilver, textPrimaryM } from 'web-ui-kit/colors';
import { SecondaryCta } from 'web-ui-kit/button';
import styled from 'styled-components/macro';

import { userIsLoggedIn } from 'redux/User';
import AddToCartActionsController from './components/AddToCartActionsController';

import analytics from 'services/analytics';
import ProductSearch from 'services/ProductSearch';
import { getProductById } from 'services/warehouse/api';
import warehouse from 'services/warehouse';
import { bugsnagError } from 'services/bugsnag';
import { SEARCH_KEY } from './state/action-constants';

import {
  SHARED_VIEW_TYPE
} from 'utils/constants/constants';

import { PRODUCT_TYPES_WITH_ADD_ONS } from './utils';

import {
  FEATURE_FLAGS,
  getFeatureFlagEnabledBool
} from 'shared/reducers/featureFlag';

import Product from 'redux/models/Product';
import {
  getActiveDesign,
  getActiveSpace,
  getActiveProduct,
  getActiveProductId,
  getIsSharedSpace,
  hasAPurchasedSpace,
  selectPDPOnlyHotspotProduct
} from 'redux/Studio';

import {
  getAlternativeProduct,
  getSelectedProduct,
  selectAltProduct,
  selectProductAndGetAndClearAlternatives,
  reset,
  getVariantLoadStatus,
  getVariantsByAttribute,
  getVariantProducts,
  fetchVariantProducts
} from './state';

import {
  SWAP_MULT_MODAL_ID
} from 'shared/components/SwapMultipleModal';
import {
  PRE_SWAP_MODAL_ID
} from 'shared/components/PreSwapModal';

import Variants from './components/Variants';
import MonarchAdapter from './MonarchAdapter';

const WALLPAPER_TYPES = ['Wallpaper', 'Wallpaper - Installation'];
export class ProductDetailsPage extends React.Component {
  constructor (props) {
    super(props);
    // Use this to tell the view not to reset scroll position
    this.lastUpdateWasFromVariant = false;
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.updateSelectedForAttribute = this.updateSelectedForAttribute.bind(this);

    this.state = {
      loadingAlternatives: false,
      loadingProductAddOns: false,
      previousProducts: [],
      productAddOns: [],
      selectedAttributes: null,
      showShippingModal: false,
      top: window.scrollY
    };
  }

  activeProductHasAllData () {
    // If the product was loaded only as a variant using /batch?id_list=
    // then we will not have the dimension info
    // so we will need to do a reload on the full product
    // to make sure we have all the info
    const { activeProduct } = this.props;
    return activeProduct &&
    activeProduct.description() &&
    (activeProduct.width() || activeProduct.height() || activeProduct.depth());
  }

  componentDidMount () {
    const {
      activeProduct,
      activeProductId
    } = this.props;
    if (!activeProduct || !this.activeProductHasAllData()) {
      getProductById(activeProductId).then(whProduct => {
        const activeProduct = Product.createProductFromWarehouseProductResponse({whProduct});
        this.setState({ activeProduct });
        this.finish(activeProduct);
      });
    } else {
      this.finish(activeProduct);
    }
    document.addEventListener('keydown', this.handleKeyPress, false);
  }

  componentWillUnmount () {
    document.removeEventListener('keydown', this.handleKeyPress, false);

    // Return to the screen Y position
    // when PDP was launched.
    window.scrollTo(0, this.state.top);

    this.props.reset();
  }

  componentDidUpdate (prevProps, prevState) {
    const { trackStudioPDPView } = this.props;
    const { selectedAttributes } = this.state;
    const prevProd = prevProps.altProduct || prevProps.product;
    const currentProd = this.props.altProduct || this.props.product;
    // on intitial product load, don't do anything
    if (!prevProd) { return; }
    // when product changes...
    if (prevProd.id() !== currentProd.id()) {
      this.lastUpdateWasFromVariant = false;

      const previousProducts = prevState.previousProducts;
      const lastPrevProd = previousProducts[previousProducts.length - 1];

      if (currentProd) {
        trackStudioPDPView({
          altProduct: prevProd,
          product: currentProd,
          referralSource: 'alternative'
        });

        this.setProductAddOns({ product: currentProd });
      }

      // The component's state keeps track of all the products previously
      // viewed while in the product details page. This will manage that
      // state so that you may go back to originally viewed items
      if (lastPrevProd && lastPrevProd.id() === currentProd.id()) {
        previousProducts.pop();
      } else {
        previousProducts.push(prevProd);
      }
      let newSelectedAttributes = null;
      if (selectedAttributes && currentProd) {
        newSelectedAttributes = currentProd.variantAttributes();
      }

      this.setState({
        previousProducts,
        selectedAttributes: newSelectedAttributes
      });
    }
  }

  setProductAddOns = async ({ product }) => {
    // For now, only specific products have add-ons.
    // In the future we may allow it for more products.
    const currentMainType = product && product.mainType();
    const isProductWithAddOns = currentMainType && PRODUCT_TYPES_WITH_ADD_ONS[currentMainType];

    if (!isProductWithAddOns) {
      return null;
    }

    try {
      // Show a loader while fetching.
      this.setState({ loadingProductAddOns: true });
      const response = await warehouse.getProductAddOns({ productId: product.id() });

      // Hide the loader and populate the state with add-ons when fetching is done.
      if (response) {
        return (
          this.setState({
            loadingProductAddOns: false,
            productAddOns: response.results
          })
        );
      }
    } catch (error) {
      bugsnagError(error);
    }
  }

  getPartnerSlug = () => {
    const { activeSpace } = this.props;
    return (activeSpace && activeSpace.partnerSlug()) || '';
  }

  handleLaunchProductAddOnPDP = (productAddOn) => {
    const { dispatchSelectAltProduct } = this.props;
    const partnerSlug = this.getPartnerSlug();
    dispatchSelectAltProduct(productAddOn, partnerSlug);

    // A product add-on cannot have add-ons associated.
    // So let's hide them.
    this.setState({ productAddOns: [] });
  }

  backToLastProduct = () => {
    const { dispatchSelectAltProduct } = this.props;
    const previousProducts = this.state.previousProducts;
    const product = previousProducts[previousProducts.length - 1];
    dispatchSelectAltProduct(
      product,
      this.getPartnerSlug()
    );
  }

  finish = async (activeProduct) => {
    const {
      onPDPMount,
      trackStudioPDPView,
      useVariants
    } = this.props;

    this.setState({ loadingAlternatives: true });
    this.setState({ loadingAlternatives: false, previousProducts: [] });

    if (activeProduct) {
      await onPDPMount(
        activeProduct,
        this.getPartnerSlug(),
        useVariants
      );
      trackStudioPDPView({
        product: activeProduct,
        referralSource: 'cart'
      });
    }

    this.setProductAddOns({ product: activeProduct });
  };

  // close the product details page on outside click excluding header clicks
  handleOutsideClick = (event) => {
    const appNavigationBar = document.getElementById('app-navigation-bar');
    const multModal = document.getElementById(SWAP_MULT_MODAL_ID);
    const preSwapModal = document.getElementById(PRE_SWAP_MODAL_ID);

    if (
      (event.target === this.outsideContainer ||
      !this.outsideContainer.contains(event.target)) &&
      (!(appNavigationBar && appNavigationBar.contains(event.target)) ||
      event.target === !appNavigationBar) &&
      !((multModal && multModal.contains(event.target)) || event.target === multModal) &&
      !((preSwapModal && preSwapModal.contains(event.target)) || event.target === preSwapModal)
    ) {
      this.props.close();
    }
  }

  toggleShippingModal = () => {
    this.setState({ showShippingModal: !this.state.showShippingModal });
  };

  handleKeyPress (e) {
    const { close } = this.props;
    const escKeyCode = 27;
    const isEscapeKey = e && e.keyCode === escKeyCode;

    isEscapeKey && close();
  }

  getPDPProduct () {
    const { altProduct, product } = this.props;
    return altProduct || product;
  }

  updateSelectedForAttribute ({ attributeType, attributeValue }) {
    this.updateProductsForSelected({ swappedType: attributeType, swappedValue: attributeValue });
  }

  getActiveStateForAttributeType ({ attributeType }) {
    const { selectedAttributes } = this.state;
    const activeProd = this.getPDPProduct();
    return get(selectedAttributes, `${attributeType}`, activeProd.variantAttributes()[attributeType]);
  }

  getFirstAttributeMatchingProductId ({ productId, attributeType }) {
    const { variantsByAttribute } = this.props;
    const valuesForType = variantsByAttribute[attributeType];
    const allValues = Object.keys(valuesForType);
    for (const index in allValues) {
      const value = allValues[index];
      if (valuesForType[value] && valuesForType[value].includes(productId)) {
        return allValues[index];
      }
    }
    return null;
  }

  /**
   * Search Func - Find a valid product based on the selected attributes
   * swappedType and swappedValue are used to update correctly before state is updated
   * swappedType is used when a product is not found as an exact match to force the update
   * of other attributes to allow a match with the swappedType
   */
  updateProductsForSelected ({ swappedType, swappedValue }) {
    const {
      activeDesign,
      activeProduct,
      activeSpace,
      dispatchSelectAltProduct,
      enabledFilters,
      product,
      trackStudioVariantClick,
      variantsByAttribute,
      variantProducts
    } = this.props;
    // Early exit if not supported or if page isn't loaded
    if (!variantsByAttribute) return;

    // Get the current product in the PDP
    const activeProd = this.getPDPProduct();

    // Get the initial product that was loaded
    const initialProduct = product || activeProduct;

    // Add the initial product to the list of products as it isn't included in variants
    const allProducts = initialProduct ? [initialProduct, ...variantProducts] : variantProducts;

    const allAttributeTypes = Object.keys(variantsByAttribute);

    // Exact matching for all attributes
    let selectedProduct = allProducts.filter(product => {
      // Select a product where all of the attributes match the selected value
      return allAttributeTypes.reduce((isSelected, attributeType) => {
        // If the attribute was just swapped use the swapped value instead of the value in state
        const isSwappedAttribute = attributeType === swappedType;
        const currentStateValue = isSwappedAttribute ? swappedValue : this.getActiveStateForAttributeType({ attributeType });
        const isInAttributeArray = variantsByAttribute[attributeType][currentStateValue].includes(product.id());
        return isSelected && isInAttributeArray;
      }, true);
    });

    if (selectedProduct && selectedProduct.length > 0) {
      // We found an exact match product, grab the first matching value from the array (should only be one match)
      selectedProduct = selectedProduct[0];
      // Update the state to match the found product
      let updatedState = {};
      allAttributeTypes.forEach(attributeType => {
        updatedState[attributeType] = selectedProduct.variantAttributes()[attributeType];
      });
      this.setState({ selectedAttributes: updatedState });
    } else {
      // No matching product was found, we need to swap the type of one
      // of the other attributes to match a product
      // Get the attributes that will need to be calculated
      const otherAttributeTypes = allAttributeTypes.filter(attributeType => attributeType !== swappedType);
      // Get the products available for the selected attribute
      const validProductIds = variantsByAttribute[swappedType][swappedValue];
      if (validProductIds && validProductIds.length > 0) {
        // Grab the first product id
        const productId = validProductIds[0];
        // Generate the next state starting with the swapped value
        let updatedState = {
          [swappedType]: swappedValue
        };
        // Add the calculated values for the other attributes
        otherAttributeTypes.forEach(attributeType => {
          updatedState[attributeType] = this.getFirstAttributeMatchingProductId({ productId, attributeType });
        });
        this.setState({ selectedAttributes: updatedState });
        // Grab the selected product from the variant products or get the initial product if it is not in variants
        selectedProduct = variantProducts.find(product => product.id() === productId) || initialProduct;
      }
    }
    if (selectedProduct && selectedProduct.id() !== activeProd.id()) {
      const index = variantProducts.findIndex(product => product.id() === selectedProduct.id()) || 0;
      trackStudioVariantClick({
        index,
        product: selectedProduct,
        design: activeDesign,
        studio: activeSpace,
        pdpAltFilterNames: enabledFilters,
        parentProduct: initialProduct,
        attributeType: swappedType,
        attributeValue: swappedValue
      });
      // Update active product
      this.lastUpdateWasFromVariant = true;
      dispatchSelectAltProduct(selectedProduct, '');
    }
  }

  handleMonarchSelectProductCard = ({ index, selectedProduct, source }) => {
    const {
      activeDesign,
      activeSpace,
      dispatchSelectAltProduct,
      enabledFilters,
      product,
      trackStudioAlternativeClick,
      trackStudioVariantClick
    } = this.props;

    const analyticsFcn = (source === 'similarProducts')
      ? trackStudioAlternativeClick
      : trackStudioVariantClick;

    analyticsFcn({
      index,
      product: selectedProduct,
      design: activeDesign,
      studio: activeSpace,
      pdpAltFilterNames: enabledFilters,
      parentProduct: product
    });
    dispatchSelectAltProduct(selectedProduct, '');
  }

  renderVariantAttributes () {
    const {
      product,
      variantsByAttribute,
      variantProducts
    } = this.props;

    // Not supported or variants not loaded
    if (!variantsByAttribute) return null;

    // Render the variant by attribute sections
    if (product && product.hasAttributeVariants()) {
      // If the product has a primary attribute type
      // Get the available product ids for the type
      const primaryAttribute = product.primaryVariantAttribute();
      const availableProductsFromPrimary = primaryAttribute
        ? variantsByAttribute[primaryAttribute][this.getActiveStateForAttributeType({ attributeType: primaryAttribute })]
        : null;
      const allProducts = [product, ...variantProducts];
      const allAttributeTypes = Object.keys(variantsByAttribute);
      return allAttributeTypes.map(attributeType => {
        const selectedValueForType = this.getActiveStateForAttributeType({ attributeType });
        const isPrimaryAttribute = attributeType === product.primaryVariantAttribute();
        const isFirst = attributeType === allAttributeTypes[0];
        const isLast = attributeType === allAttributeTypes[allAttributeTypes.length - 1];

        const attributeValues = variantsByAttribute[attributeType];
        const attributeValueTypes = attributeValues ? Object.keys(attributeValues) : [];

        const shouldRender = attributeValueTypes.length > 1;

        return shouldRender
          ? (
            <Variants
              key={attributeType}
              availableProductsFromPrimary={availableProductsFromPrimary}
              isPrimary={isPrimaryAttribute}
              isFirst={isFirst}
              isLast={isLast}
              updateSelectedForAttribute={this.updateSelectedForAttribute}
              selectedValueForType={selectedValueForType}
              attributeType={attributeType}
              attributeValues={attributeValues}
              variantProducts={allProducts}
            />
          )
          : null;
      });
    }
  }

  render () {
    this.panoContainer = document.getElementById('panoramaContainer');

    if (this.panoContainer) {
      return ReactDOM.createPortal(this.renderPDP(), this.panoContainer);
    }

    return this.renderPDP();
  }

  renderPDP () {
    const {
      activeSpace,
      displayedAlternatives,
      freeShippingMessageEnabled,
      fromCart,
      fromShop,
      isRedesign,
      onAddToCart,
      onLikeDislikeProduct,
      product,
      searchOrigin,
      variantsByAttribute,
      variantsLoading,
      variantProducts
    } = this.props;

    const {
      loadingAlternatives
    } = this.state;

    if (!product) { return null; }

    const prod = this.getPDPProduct();

    const uiMessaging = product && product.uiMessaging();

    const isBackOrdered = product.isBackOrdered();

    const isFreeShipping = product.isFreeShipping();

    const isOutOfStock = product && (product.isOutOfStock() && !isBackOrdered);

    const isSellable = product && product.isSellable();

    const isMaterial = product && product.isMaterial();

    const isPromoExcluded = product && product.isPromoExcluded();

    const isWallpaper = product && WALLPAPER_TYPES.includes(product.mainType());

    return (
      <AddToCartActionsController
        closePDP={this.props.close}
        isBackOrdered={isBackOrdered}
        isOutOfStock={isOutOfStock}
        isRedesign={isRedesign}
        onAddToCart={onAddToCart}
        searchOrigin={searchOrigin}
      >
        <MonarchAdapter
          // data
          activeProduct={prod}
          activeSpace={activeSpace}
          alternativeProducts={displayedAlternatives}
          alternativeProductsLoading={loadingAlternatives}
          atcQuantity={prod.getQuantityMultiple() || 1}
          fromCart={fromCart}
          fromShop={fromShop}
          initialProduct={product}
          isFreeShipping={isFreeShipping}
          isMaterial={isMaterial}
          isPromoExcluded={isPromoExcluded}
          isSellable={isSellable}
          isWallpaper={isWallpaper}
          productAddOns={this.state.productAddOns}
          productAddOnsLoading={this.state.loadingProductAddOns}
          spaceId={activeSpace && activeSpace.id()}
          uiMessaging={uiMessaging}
          variantsByAttribute={variantsByAttribute}
          variantsLoading={variantsLoading}
          variantProducts={variantProducts}

          // feature flags
          freeShippingMessageEnabled={freeShippingMessageEnabled}

          // callbacks and event handling
          onAddToCart={onAddToCart}
          onBack={this.backToLastProduct}
          onClose={this.props.close}
          onLikeDislikeProduct={onLikeDislikeProduct}
          onProductAddOnClick={this.handleLaunchProductAddOnPDP}
          onSelectAddonProduct={this.handleLaunchProductAddOnPDP}
          onSelectAltProduct={this.handleMonarchSelectProductCard}
          onUpdateSelectedAttribute={this.updateSelectedForAttribute}
        />
      </AddToCartActionsController>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const isSharedSpace = getIsSharedSpace();
  const activeDesign = getActiveDesign({ state });
  const activeProductId = ownProps.activeProduct
    ? ownProps.activeProduct.id()
    : getActiveProductId(state);

  const searchProduct = SEARCH_KEY + activeProductId;

  const allow2DProductsEnabled = getFeatureFlagEnabledBool(FEATURE_FLAGS.Include2DProducts, state);
  const freeShippingMessageEnabled = getFeatureFlagEnabledBool(FEATURE_FLAGS.ShowFreeShippingMessage, state);

  return {
    activeProductId,
    allow2DProductsEnabled,
    activeProduct: ownProps.activeProduct || getActiveProduct({ state }),
    activeSpace: getActiveSpace({ state }),
    activeDesign,
    altProduct: getAlternativeProduct(state),
    displayedAlternatives: ProductSearch.getSearchResultFor({ name: searchProduct, state }),
    enabledFilters: ProductSearch.getEnabledFiltersFor({ name: searchProduct, state }),
    freeShippingMessageEnabled,
    hasAPurchasedSpace: hasAPurchasedSpace(state),
    isSearchRunning: ProductSearch.isRunning({name: searchProduct, state}),
    isIPad: state.device.isIPad,
    isPhone: state.device.isPhone,
    isScreenWidthSmall: state.device.isScreenWidthSmall,
    isSharedSpace,
    loggedIn: userIsLoggedIn(state),
    product: getSelectedProduct(state),
    screenHeight: state.device.screenHeight,
    screenWidth: state.device.screenWidth,
    variantsLoading: getVariantLoadStatus(state),
    variantProducts: getVariantProducts(state),
    variantsByAttribute: getVariantsByAttribute(state)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  dispatchSelectAltProduct (product, partnerSlug) {
    dispatch(selectAltProduct(product, partnerSlug));
  },
  close () {
    // Clear the PDP hotspot data if there is any
    dispatch(selectPDPOnlyHotspotProduct(null, null));

    // First, check for client controlled close behavior.
    if (typeof ownProps.onClose === 'function') {
      ownProps.onClose();
      return;
    }

    const { additionalOnCloseParams = {} } = ownProps;
    // When PDP page closes, we should navigate back to the active space and design id.
    // TODO: we might be navigating back to a redesign.
    const { spaceId, designId } = ownProps.match.params;
    const viewType = getIsSharedSpace() ? SHARED_VIEW_TYPE : undefined;
    ownProps.close({ spaceId, designId, viewType, doNotScrollTop: true, ...additionalOnCloseParams });
  },
  onPDPMount (activeProduct, partnerSlug, useVariants = false) {
    if (useVariants && !activeProduct.isMaterial()) {
      dispatch(fetchVariantProducts(activeProduct, partnerSlug));
    }

    // The active product is controlled by the URL product ID params
    // which is owned by react-router. So when PDP component mounts
    // we need to select the active product.
    dispatch(selectProductAndGetAndClearAlternatives(activeProduct, partnerSlug, null));
  },
  reset () {
    dispatch(reset());
  },
  selectProductAddOn (productAddOn, partnerSlug) {
    dispatch(selectAltProduct(productAddOn, partnerSlug));
  },
  trackStudioPDPView (payload) {
    // Shop handles its own PDP View tracking.
    if (ownProps.searchOrigin === 'shop') {
      return;
    }

    dispatch(analytics.trackStudioPDPView(payload));
  },
  trackStudioVariantClick (payload) {
    dispatch(analytics.trackStudioVariantClick(payload));
  },
  trackStudioAlternativeClick (payload) {
    dispatch(analytics.trackStudioPDPAlternativeProductsClicked(payload));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductDetailsPage);

const MainContainer = styled(Flex)`
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  height: ${props => props.height}px;
  width: 100vw;
  max-width: 100vw;
  background: rgba(0, 0, 0, 0.6);
  touch-action: auto;
  pointer-events: all;
  /* Needs to be on top of Checkout/PDP - review web-ui-kit constants before updating) */
  z-index: ${PDP_MODAL_Z_INDEX};

  box-sizing: border-box;
`;
MainContainer.displayName = 'MainContainer';

const Container = styled(Box)`
  background: white;
  height: ${props => {
    const {
      screenHeight = 0,
      isPhone = false
    } = props;

    return isPhone ? screenHeight : screenHeight * 0.80;
  }}px;
  max-width: 1230px;
  overflow-y: scroll;
  overflow-x: hidden;
  position: relative;
  -webkit-overflow-scrolling: touch;
  width: calc(100vw - 80px);
  margin-top: ${props => {
    const defaultNavHeight = 0;
    const {
      navBarHeight = defaultNavHeight,
      studioBannerHeight = defaultNavHeight,
      useNavMargin = true
    } = props;
    const totalNavHeight = navBarHeight + studioBannerHeight;

    return useNavMargin ? totalNavHeight : defaultNavHeight;
  }}px;

  @media (max-width: 767px) {
    ${props => props.fromCart && `height: ${props.screenHeight}px;`}
    max-width: 100%;
    width: 100vw;
  }

  ${props => props.isIPad && `
    @media (orientation: portrait) {
      width: 100vw;
    }
  `}
`;
Container.displayName = 'Container';

const ProductDetailsContainer = styled.div`
  margin-bottom: 24px;
`;
ProductDetailsContainer.displayName = 'ProductDetailsContainer';

const DetailsColumn = styled(Col)`
  padding: 54px 54px 0px 22.5px;
  box-sizing: border-box;
  @media (max-width: 767px) {
    padding: 25px 20px 15px;
  }
`;
DetailsColumn.displayName = 'DetailsColumn';

const SpecialMessaging = styled(H7)`
  line-height: 20px;
  margin-top: 8px;
`;
SpecialMessaging.displayName = 'SpecialMessaging';

const Message2DProduct = styled.div`
  border-top: 1px solid ${burnishedSilver};
  padding-top: 1.5rem;
  margin: 1.5rem 0 1.5rem;
  font-family: ${primaryFont};
  font-size: 14px;
  line-height: 1.5rem;

  > span {
    color: ${textPrimaryM};
    font-size: 16px;
    font-weight: 600;
  }

  > div {
    margin-top: 0.7rem;
  }

  > div:nth-of-type(2) {
    margin-top: 1rem;
  }
`;

Message2DProduct.displayName = 'Message2DProduct';

const PurchaseAddonCta = styled(SecondaryCta)`
  height: 2.5rem;
  margin: 1rem 0 0;
`;

PurchaseAddonCta.displayName = 'PurchaseAddonCta';

const OutOfStockMessaging = styled.p`
  font-size: 0.875rem;
  line-height: 1.25rem;
  white-space: pre-wrap;
`;
OutOfStockMessaging.displayName = 'OutOfStockMessaging';

const ExternalLink = styled.a`
  color: ${textPrimaryM};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;
ExternalLink.displayName = 'ExternalLink';
